import { z } from 'zod';

/**
 * Entity types that support attachments
 */
export const entityTypeEnum = z.enum([
  'logo',
  'product',
  'po',
  'grn',
  'variant',
  'misc',
]);
export type EntityType = z.infer<typeof entityTypeEnum>;

/**
 * Attachment schema - represents a file attachment in the system
 */
export const attachmentSchema = z.object({
  id: z.string().uuid(),
  entity_type: entityTypeEnum,
  entity_id: z.string().uuid(),
  file_name: z.string().min(1, 'File name is required'),
  file_size: z.number().positive('File size must be positive'),
  file_type: z.string().min(1, 'File type is required'),
  s3_key: z.string().min(1, 'S3 key is required'),
  uploaded_by: z.string().uuid(),
  uploaded_at: z.string().datetime(),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
});

export type Attachment = z.infer<typeof attachmentSchema>;

/**
 * Supported file MIME types by category
 */
export const SUPPORTED_FILE_TYPES = {
  images: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'],
  documents: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  spreadsheets: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ],
  text: ['text/plain', 'text/markdown'],
} as const;

/**
 * All supported MIME types
 */
export const ALL_SUPPORTED_TYPES = [
  ...SUPPORTED_FILE_TYPES.images,
  ...SUPPORTED_FILE_TYPES.documents,
  ...SUPPORTED_FILE_TYPES.spreadsheets,
  ...SUPPORTED_FILE_TYPES.text,
];

/**
 * File size limits by category (in bytes)
 */
export const FILE_SIZE_LIMITS = {
  images: 5 * 1024 * 1024, // 5MB
  documents: 10 * 1024 * 1024, // 10MB
  spreadsheets: 10 * 1024 * 1024, // 10MB
  text: 5 * 1024 * 1024, // 5MB
} as const;

/**
 * Maximum file size (10MB)
 */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * File upload schema with validation
 */
export const uploadAttachmentSchema = z.object({
  entity_type: entityTypeEnum,
  entity_id: z.string().uuid('Invalid entity ID'),
  file: z
    .instanceof(File)
    .refine(
      (file) => file.size <= MAX_FILE_SIZE,
      `File size must be less than ${MAX_FILE_SIZE / 1024 / 1024}MB`
    )
    .refine(
      (file) => ALL_SUPPORTED_TYPES.includes(file.type as (typeof ALL_SUPPORTED_TYPES)[number]),
      'Invalid file type. Supported: PDF, Images, Excel, Word, Text files'
    ),
});

export type UploadAttachment = z.infer<typeof uploadAttachmentSchema>;

/**
 * Helper function to check if a file type is supported
 */
export function isSupportedFileType(mimeType: string): boolean {
  return ALL_SUPPORTED_TYPES.includes(mimeType as (typeof ALL_SUPPORTED_TYPES)[number]);
}

/**
 * Helper function to get file category from MIME type
 */
export function getFileCategory(
  mimeType: string
): 'images' | 'documents' | 'spreadsheets' | 'text' | 'other' {
  if (
    SUPPORTED_FILE_TYPES.images.includes(mimeType as (typeof SUPPORTED_FILE_TYPES.images)[number])
  )
    return 'images';
  if (
    SUPPORTED_FILE_TYPES.documents.includes(
      mimeType as (typeof SUPPORTED_FILE_TYPES.documents)[number]
    )
  )
    return 'documents';
  if (
    SUPPORTED_FILE_TYPES.spreadsheets.includes(
      mimeType as (typeof SUPPORTED_FILE_TYPES.spreadsheets)[number]
    )
  )
    return 'spreadsheets';
  if (SUPPORTED_FILE_TYPES.text.includes(mimeType as (typeof SUPPORTED_FILE_TYPES.text)[number]))
    return 'text';
  return 'other';
}

/**
 * Helper function to get max file size for a MIME type
 */
export function getMaxFileSize(mimeType: string): number {
  const category = getFileCategory(mimeType);
  return category === 'other' ? MAX_FILE_SIZE : FILE_SIZE_LIMITS[category];
}

/**
 * Helper function to format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}

/**
 * Helper function to get file extension from filename
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Helper function to validate file before upload
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  // Check file type
  if (!isSupportedFileType(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Supported: PDF, Images, Excel, Word, Text files',
    };
  }

  // Check file size
  const maxSize = getMaxFileSize(file.type);
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size must be less than ${formatFileSize(maxSize)}`,
    };
  }

  return { valid: true };
}
